import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

const TOP_N_OPTIONS = [5, 10, 15, 20];

interface SectorAcquisitionItem {
    sector: string;
    count: number;
    percentage: number;
}

interface SectorAcquisitionScatterRow {
    sector: string;
    acquisitions_count: number;
    percentage?: number;
}

interface SectorAcquisitionSummary {
    total_acquisitions: number;
    sectors_with_data: number;
    top_sector: string;
    top_sector_count: number;
}

interface SectorAcquisitionApiData {
    summary?: SectorAcquisitionSummary;
    scatter_data?: SectorAcquisitionScatterRow[];
    no_acquisitions_per_scatter?: SectorAcquisitionScatterRow[];
}

export class TraderverseNumberOfAcquisitionsPerSector {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartData: SectorAcquisitionItem[] = [];
    private apiData: SectorAcquisitionApiData | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private filters = {
        topN: 10
    };

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.filters.topN = Number((config as any).topN ?? (this.config as any).topN ?? this.filters.topN);
        (this.config as any).topN = this.filters.topN;
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            topN: 10
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private renderTopNOptions(): string {
        const options = TOP_N_OPTIONS.includes(this.filters.topN)
            ? TOP_N_OPTIONS
            : [...TOP_N_OPTIONS, this.filters.topN].sort((a, b) => a - b);
        return options
            .map(n => `<option value="${n}"${n === this.filters.topN ? ' selected' : ''}>${n}</option>`)
            .join('');
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Number of Acquisitions per Sector</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              <span></span>
              <div class="filtersBox sideBar hidden">
                <div class="item">
                  <label>Top N</label>
                  <select class="topn-select" aria-label="Top N" data-testing-id="topn-select">${this.renderTopNOptions()}</select>
                </div>
              </div>
            </div>
            <div class="chartDv" id="numberOfAcquisitionsPerSector">
                <div class="insiderVolumeScale">
                    <div class="insiderVolumeScaleBar"></div>
                    <div class="insiderVolumeScaleTicks" id="sectorAcquisitionsScaleTicks"></div>
                </div>
                <div id="chartContainer" class="chart insiderVolumeChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
        this.wireFilterControls();
    }

    private wireFilterControls(): void {
        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const topNSelect = this.shadowRoot.querySelector('.topn-select') as HTMLSelectElement | null;

        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const applyFiltersAndFetch = () => {
            this.filters.topN = Number(topNSelect?.value || this.filters.topN);
            (this.config as any).topN = this.filters.topN;
            this.setupChart();
        };

        topNSelect?.addEventListener('change', applyFiltersAndFetch);
    }

    private buildQueryParams(): Record<string, string | number> {
        return {
            topN: this.filters.topN,
            limit: this.filters.topN
        };
    }

    private getScatterRows(data: SectorAcquisitionApiData | null): SectorAcquisitionScatterRow[] {
        if (!data) return [];
        return data.no_acquisitions_per_scatter ?? data.scatter_data ?? [];
    }

    private mapApiData(data: SectorAcquisitionApiData): SectorAcquisitionItem[] {
        return this.getScatterRows(data)
            .map((item) => ({
                sector: String(item.sector ?? '').trim(),
                count: Number(item.acquisitions_count) || 0,
                percentage: Number(item.percentage) || 0
            }))
            .filter((item) => item.sector && item.count > 0)
            .sort((a, b) => b.count - a.count)
            .slice(0, this.filters.topN || 10);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            this.injectScaleLegendStyles();
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#numberOfAcquisitionsPerSector') as HTMLElement;
        if (widgetBox) widgetBox.classList.add('loading');

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/mergers-aquisitions/sector-acquisitions`,
                query: this.buildQueryParams(),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            if (json && typeof json.isSuccess === 'boolean' && !json.isSuccess) {
                throw new Error(json.message || 'API returned isSuccess: false');
            }

            this.apiData = (json?.data ?? null) as SectorAcquisitionApiData | null;
            this.chartData = this.apiData ? this.mapApiData(this.apiData) : [];

            if (this.chartData.length) {
                containerWrapper?.setAttribute('data-testing-id', 'number-of-acquisitions-per-sector');
            } else {
                containerWrapper?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', { topN: this.filters.topN, data: this.chartData, summary: this.apiData?.summary });
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.chartData = [];
            this.apiData = null;
            containerWrapper?.removeAttribute('data-testing-id');
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widgetBox) widgetBox.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private getNiceAxisMax(maxValue: number): number {
        if (maxValue <= 0) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
        const normalized = maxValue / magnitude;
        const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
        return niceNormalized * magnitude;
    }

    private formatScaleValue(value: number): string {
        if (value >= 1000) {
            const thousands = value / 1000;
            return Number.isInteger(thousands) ? `${thousands}k` : `${thousands.toFixed(1)}k`;
        }
        return String(Math.round(value));
    }

    private getNeutralLabelColor(textColor: string): string {
        const trimmed = textColor.trim();
        return trimmed || '#B8B3C9';
    }

    private getGradientColors(): string[] {
        return ['#57307A', '#6E2B62', '#8F2E45', '#B8432A', '#D9791A', '#E8A820', '#F5D020', '#FFEC00'];
    }

    private renderVolumeScaleLegend(maxValue: number): void {
        const ticksEl = this.shadowRoot.querySelector('#sectorAcquisitionsScaleTicks') as HTMLElement | null;
        if (!ticksEl) return;

        const tickCount = 5;
        const labels = Array.from({ length: tickCount + 1 }, (_, index) =>
            this.formatScaleValue((maxValue / tickCount) * index)
        );

        ticksEl.innerHTML = labels.map((label) => `<span>${label}</span>`).join('');
    }

    private updateWidgetLayout(chartHeight: number): void {
        const scaleLegendHeight = 48;
        const chartDv = this.shadowRoot.querySelector('#numberOfAcquisitionsPerSector') as HTMLElement | null;
        const inBody = this.shadowRoot.querySelector('.inBody') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        const chartDvHeight = scaleLegendHeight + chartHeight;
        if (chartDv) {
            chartDv.style.height = `${chartDvHeight}px`;
            chartDv.style.minHeight = `${chartDvHeight}px`;
            chartDv.style.flex = '0 0 auto';
        }

        if (this.config.autosize === false) return;

        const optionsRow = this.shadowRoot.querySelector('.optionsRow') as HTMLElement | null;
        const headTop = this.shadowRoot.querySelector('.headTop') as HTMLElement | null;
        const optionsRowHeight = optionsRow?.offsetHeight ?? 50;
        const headTopHeight = headTop?.offsetHeight ?? 56;
        const inBodyPadding = 30;
        const inBodyHeight = optionsRowHeight + chartDvHeight + inBodyPadding;

        if (inBody) {
            inBody.style.height = `${inBodyHeight}px`;
            inBody.style.minHeight = `${inBodyHeight}px`;
        }
        if (widgetBox) {
            widgetBox.style.height = `${headTopHeight + inBodyHeight}px`;
        }
    }

    private injectScaleLegendStyles(): void {
        const existing = this.shadowRoot.querySelector('style[data-sector-acquisitions-scale]');
        if (existing) existing.remove();

        const style = document.createElement('style');
        style.setAttribute('data-sector-acquisitions-scale', 'true');
        style.textContent = `
            #numberOfAcquisitionsPerSector.chartDv {
                height: auto;
                min-height: 400px;
                display: flex;
                flex-direction: column;
                flex: 0 0 auto;
                overflow: visible;
            }
            .insiderVolumeScale {
                width: 100%;
                max-width: 420px;
                padding: 4px 0 12px;
                box-sizing: border-box;
                flex: 0 0 auto;
            }
            .insiderVolumeScaleBar {
                width: 100%;
                height: 12px;
                border-radius: 6px;
                background: linear-gradient(90deg, #57307A 0%, #6E2B62 14%, #8F2E45 28%, #B8432A 42%, #D9791A 57%, #E8A820 71%, #F5D020 85%, #FFEC00 100%);
            }
            .insiderVolumeScaleTicks {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                margin-top: 6px;
                color: var(--charts-text-color, #9A95AC);
                font-size: var(--font-size-sm, 12px);
                line-height: 1;
            }
            .insiderVolumeScaleTicks span {
                flex: 1;
                text-align: center;
            }
            .insiderVolumeScaleTicks span:first-child,
            .insiderVolumeScaleTicks span:last-child {
                flex: 0 0 auto;
            }
            #numberOfAcquisitionsPerSector .insiderVolumeChart {
                position: relative !important;
                left: auto !important;
                top: auto !important;
                width: 100%;
                height: 400px;
                flex: 0 0 auto;
                min-height: 400px;
            }
            .widgetBox:has(#numberOfAcquisitionsPerSector) .inBody {
                height: auto;
                min-height: 480px;
                overflow: visible;
            }
        `;
        this.shadowRoot.appendChild(style);
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#numberOfAcquisitionsPerSector') as HTMLElement;

        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const chartData = [...this.chartData];
        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            containerWrapper?.removeAttribute('data-testing-id');
            return;
        }

        container.innerHTML = '';
        containerWrapper?.setAttribute('data-testing-id', 'number-of-acquisitions-per-sector');

        const maxCount = Math.max(...chartData.map((item) => item.count), 1);
        const scaleMax = this.getNiceAxisMax(maxCount);
        this.renderVolumeScaleLegend(scaleMax);

        const chartHeight = 400;
        container.style.height = `${chartHeight}px`;
        container.style.minHeight = `${chartHeight}px`;
        container.style.width = '100%';
        this.updateWidgetLayout(chartHeight);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const textColor = widgetStyles.getPropertyValue('--charts-text-color');
        const splitLineColor = widgetStyles.getPropertyValue('--charts-split-line-color');
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const fontSizeMd = widgetStyles.getPropertyValue('--font-size-md').trim() || '14px';
        const labelFontSize = parseInt(fontSize, 10) || 12;
        const neutralLabelColor = this.getNeutralLabelColor(textColor);
        const summary = this.apiData?.summary;

        const gradientColors = this.getGradientColors();
        const scatterData = chartData.map((item, index) => {
            const ratio = Math.min(item.count / scaleMax, 1);
            const colorIndex = Math.min(Math.floor(ratio * (gradientColors.length - 1)), gradientColors.length - 1);
            const symbolSize = 14 + ratio * 16;
            return {
                value: [index, item.count],
                symbolSize,
                itemStyle: {
                    color: gradientColors[colorIndex],
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderWidth: 1
                }
            };
        });

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const idx = params.value[0];
                    const row = chartData[idx];
                    return `
                    <div style="font-size:${fontSize}; line-height:1.45; max-width:280px;">
                        <div style="font-size:${fontSizeMd}; font-weight:600; margin-bottom:4px;">${row.sector}</div>
                        <div>Acquisitions: <b>${row.count.toLocaleString()}</b></div>
                        ${row.percentage
                            ? `<div>Share: <b>${row.percentage.toFixed(2)}%</b></div>`
                            : ''}
                        ${summary?.total_acquisitions != null
                            ? `<div style="margin-top:6px;opacity:.8">Total: <b>${summary.total_acquisitions.toLocaleString()}</b></div>`
                            : ''}
                    </div>
                `;
                }
            },
            grid: { left: 16, right: 30, top: 16, bottom: 60, containLabel: true },
            xAxis: {
                type: 'category',
                data: chartData.map(d => d.sector),
                axisLabel: {
                    color: neutralLabelColor,
                    fontSize: labelFontSize,
                    interval: 0,
                    margin: 12,
                    width: 80,
                    overflow: 'break'
                },
                axisLine: {
                    show: true,
                    lineStyle: { color: splitLineColor }
                },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: scaleMax,
                splitNumber: 5,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10,
                    formatter: (value: number) => this.formatScaleValue(value)
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dashed'
                    }
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series: [
                {
                    type: 'scatter',
                    data: scatterData,
                    emphasis: {
                        itemStyle: {
                            borderColor: '#fff',
                            borderWidth: 2,
                            shadowBlur: 10,
                            shadowColor: 'rgba(0,0,0,0.3)'
                        }
                    }
                }
            ],
            animation: true,
            animationDuration: 1000,
            animationEasing: 'cubicOut'
        };

        const chart = echarts.init(container, null, { renderer: 'canvas' });
        chart.setOption(option, true);
        chart.resize({ width: container.clientWidth || undefined, height: chartHeight });

        requestAnimationFrame(() => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        });

        const resizeHandler = () => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
            this.updateWidgetLayout(chartHeight);
        };
        window.addEventListener('resize', resizeHandler);
        chart.on('disposed', () => {
            window.removeEventListener('resize', resizeHandler);
        });
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseNumberOfAcquisitionsPerSector: typeof TraderverseNumberOfAcquisitionsPerSector; } }
if (typeof window !== 'undefined') { window.TraderverseNumberOfAcquisitionsPerSector = TraderverseNumberOfAcquisitionsPerSector; }
